import React, { useState, useEffect, useMemo } from 'react';
import { useImmerReducer } from 'use-immer';
import { PropTypes } from 'prop-types';
import { Alert, Grid, Tabs, Tab, styled } from '@nextiva/next-ui-components';
import { getDomains, removeDomain } from 'api/domainApi';
import { getEmails } from 'api/emailApi';
import {
  ActionHeader,
  DomainTableHeader,
  DomainList,
  withRouterQuery,
  TabPanel,
} from 'components';
import { Page } from 'containers';
import { useSnackbar } from 'hooks';
import { toggleState, useStateWithLocalStorage } from 'utils/state-helpers';

import { EmailContext, InitialState, actionTypes } from './emailContext';
import { reducer } from './reducer';
import VerifyCode from './VerifyCode';
import VerifyNewDomain from './VerifyNewDomain';
import AuthenticateDomain from './AuthenticateDomain';
import RemoveDomainModal from './RemoveDomainModal';

const buttonStyle = {
  width: '172px',
  height: '40px',
  padding: '0px',
};

const TabMargin = styled('div')`
  margin-top: 28px;
`;

export const Email = ({ routeParams: { email = '' } }) => {
  const [emailState, dispatch] = useImmerReducer(reducer, InitialState);
  const [pendingEmail, setPendingEmail] = useState(email);
  const [showVerifyCodeDrawer, setShowVerifyCodeDrawer] = useState(
    pendingEmail !== '',
  );
  const [showVerifyDomainDrawer, setShowVerifyDomainDrawer] = useState(false);
  const [tabIndex, setTabIndex] = useStateWithLocalStorage('emailTabIndex', 0);
  const [showNotification, setShowNotification] = useStateWithLocalStorage(
    'emailShowNotification',
    true,
  );
  const [showModal, setShowModal] = useState(false);
  const [showAuthenticationDrawer, setShowAuthenticationDrawer] = useState(
    false,
  );

  const setSnackbar = useSnackbar();

  const { selectedDomain } = emailState;

  const getDomainsList = () => {
    getDomains()
      .then((domainsList) => {
        dispatch({
          type: actionTypes.SET_DOMAINS_LIST,
          domainsList,
        });
        dispatch({
          type: actionTypes.SET_DOMAINS_LIST_IS_LOADING,
          isLoading: false,
        });
      })
      .catch(() => {
        // TODO: add notification on error
      });
  };

  const getEmailsList = () => {
    getEmails()
      .then((emailsList) => {
        dispatch({
          type: actionTypes.SET_EMAILS_LIST,
          emailsList,
        });
        dispatch({
          type: actionTypes.SET_EMAILS_LIST_IS_LOADING,
          isLoading: false,
        });
      })
      .catch(() => {
        // TODO: add notification on error
      });
  };

  const value = useMemo(
    () => ({
      emailState,
      dispatch,
    }),
    [emailState, dispatch],
  );

  const handleVerifyCodeSuccess = () => {
    setPendingEmail('');
    setShowVerifyCodeDrawer(false);
    setSnackbar({
      message: 'Done. Your domain is verified.',
      severity: 'success',
    });
    getDomainsList();
  };

  const handleVerifyDomainSuccess = (emailAddr) => {
    setPendingEmail(emailAddr);
    setShowVerifyDomainDrawer(false);
    setShowVerifyCodeDrawer(true);
  };

  const handleTabChange = (e, newValue) => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    getEmailsList();
    getDomainsList();
  }, []);

  return (
    <Page title="Email" className="m-email">
      <EmailContext.Provider value={value}>
        <Grid className="m-tabs" container>
          <Grid item xs={12}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              data-testid="PlatformAdminUI-Email-Tabs"
            >
              <Tab label="Verify domains" />
            </Tabs>
          </Grid>
          <Grid item xs={12}>
            {/* Verify Domain Panel */}
            <TabPanel value={tabIndex} index={0}>
              <div className="tab-panel">
                {showNotification && emailState.defaultDomain.length > 0 && (
                  <div className="m-email-info">
                    <Alert
                      onClose={toggleState(setShowNotification)}
                      severity="info"
                    >
                      <strong>{emailState.defaultDomain}</strong>is already set
                      up for you to use. To use your own email address domain,
                      you can add and verify it below. Additionally, we
                      recommend you authenticate your domain to ensure your
                      emails stay out of your customer’s junk mail.
                    </Alert>
                  </div>
                )}
                <ActionHeader
                  label="Domains"
                  actionName="Verify new domain"
                  buttonStyle={buttonStyle}
                  color="primary"
                  data-testid="PlatformAdminUI-Email-VerifyDomain"
                  onClick={toggleState(setShowVerifyDomainDrawer)}
                />
                <TabMargin>
                  <VerifyNewDomain
                    handleSuccess={handleVerifyDomainSuccess}
                    open={showVerifyDomainDrawer}
                    toggleDrawer={toggleState(setShowVerifyDomainDrawer)}
                  />
                  <VerifyCode
                    handleSuccess={handleVerifyCodeSuccess}
                    initialEmail={pendingEmail}
                    open={showVerifyCodeDrawer}
                    toggleDrawer={toggleState(setShowVerifyCodeDrawer)}
                  />
                  <AuthenticateDomain
                    showAuthenticationDrawer={showAuthenticationDrawer}
                    toggleDrawer={toggleState(setShowAuthenticationDrawer)}
                    selectedDomain={selectedDomain}
                  />
                  <DomainTableHeader />
                  <DomainList
                    getDomainsList={getDomainsList}
                    toggleModal={toggleState(setShowModal)}
                    toggleDrawer={toggleState(setShowAuthenticationDrawer)}
                  />
                </TabMargin>
              </div>
            </TabPanel>
          </Grid>
        </Grid>
        <RemoveDomainModal
          toggleModal={toggleState(setShowModal)}
          removeDomain={removeDomain}
          showModal={showModal}
          selectedDomain={selectedDomain}
          getDomainsList={getDomainsList}
        />
      </EmailContext.Provider>
    </Page>
  );
};

Email.propTypes = {
  routeParams: PropTypes.shape({
    email: PropTypes.string,
  }),
};

Email.defaultProps = {
  routeParams: {},
};

export default withRouterQuery(Email);
